import "./dashboard-route-skeleton.css";

/**
 * Suspense fallback for the lazily-loaded signed-in routes.
 *
 * Sign-in is an OAuth *hard* navigation, so arriving at /dashboard is a cold boot: the
 * bundle runs, auth hydrates, and only then does the DashboardPage chunk start
 * downloading. The boundary's previous `fallback={null}` left that whole stretch as a
 * bare white body — 1-2s of it on a real network.
 *
 * The reasoning that made `null` look free ("ProtectedRoute already renders nothing
 * while auth hydrates") holds for a warm in-app navigation, where the chunk fetch
 * genuinely overlaps the auth round trip. On the post-OAuth cold boot there is no
 * pre-existing blank period to hide inside: the chunk fetch *extends* the boot.
 *
 * This mirrors the .dash shell geometry — same sidebar width, same header band — so the
 * real page replaces it without shifting layout. It deliberately does NOT imitate a
 * hero mid-fade the way a landing-page fallback would have to (see the #boot-fallback
 * comment in index.html): the dashboard paints its chrome immediately, so there is no
 * opacity-0 first frame to stay in lockstep with, and therefore no flash to trade for.
 */
export function DashboardRouteSkeleton() {
  return (
    <div className="dash-root">
      <div className="drs" aria-hidden="true">
        <aside className="drs-side">
          <div className="drs-brand">
            <span className="drs-box drs-brand-mark ui-skeleton" />
            <span className="drs-lines">
              <span className="drs-box drs-line drs-line-name ui-skeleton" />
              <span className="drs-box drs-line drs-line-sub ui-skeleton" />
            </span>
          </div>
          <div className="drs-nav">
            {Array.from({ length: 6 }, (_, i) => (
              <span key={i} className="drs-box drs-navitem ui-skeleton" />
            ))}
          </div>
        </aside>

        <main className="drs-main">
          <div className="drs-top">
            <span className="drs-box drs-title ui-skeleton" />
            <span className="drs-box drs-action ui-skeleton" />
          </div>
          <div className="drs-body">
            {Array.from({ length: 3 }, (_, i) => (
              <span key={i} className="drs-box drs-card ui-skeleton" />
            ))}
          </div>
        </main>
      </div>
      {/* The visual layer is decorative; this is what a screen reader announces. */}
      <span className="drs-sr" role="status" aria-live="polite">Loading your workspace…</span>
    </div>
  );
}
